import { useEffect, useRef, useState } from "react";
import BreadCrumbs from "../../components/General/BreadCrumbs";
import ShowroomHero from "../../components/Showrooms/ShowroomHero";
import { getSingleShowroom, getBannersFiltered } from "../../utils/fetcher";
import SidebarBox from "../../components/General/SidebarBox";
import ShowroomSocial from "../../components/Showrooms/ShowroomSocial";
import LoadingSkeleton from "../../components/UI/LoadingSkeleton";
import { VehicleTypesT, vehicleModulesMap } from "../../utils/GeneralTypes";
import SingleUsedVehicleCard from "../../components/Vehicles/SingleUsedVehicleCard";
import style from "./index.module.css";
import { VehicleTypeSelector } from "../../components/General/VehiclesControllers";
import { useScrollToTop } from "../../utils/generalUtils";
import Banner from "../../components/Vehicles/Banner";
import { useTranslation } from "../../hooks/useTranslation";
import EmptyContent from "../../components/General/EmptyContent";
import { useParams } from "react-router-dom";

type ComponentDataT = {
  center: any;
  working_in: any;
  brands: any[];
  models: any[];
  currentBrand: any;
  extraBreadCrumbs: { text: string; link: string }[];
  banners: any;
  /**
   * Stores all vehicle data
   */
  vehiclesData: any;
  /**
       * Stores available vehicle types

       */
  availableVehicleTypes: any[];
  /**
   * Store selected vehicle type
   */
  selectedType: VehicleTypesT;
};

const SingleShowroom = () => {
  const { centerID } = useParams<{ centerID: string }>();
  const { locale } = useTranslation();

  const initialMount = useRef<boolean>(true);

  useScrollToTop();

  const [componentData, setComponentData] = useState<{
    isFetching: boolean;
    data: ComponentDataT;
  }>({
    isFetching: true,
    data: {
      center: null,
      working_in: null,
      brands: [],
      models: [],
      currentBrand: null,
      extraBreadCrumbs: [],
      banners: null,
      vehiclesData: {}, // Stores all vehicle data
      availableVehicleTypes: [], // Stores available vehicle types
      selectedType: "bus", // Default to show bus
    },
  });

  useEffect(() => {
    if (!initialMount.current) {
      setComponentData((current) => ({
        ...current,
        isFetching: true,
      }));
    }
    const data: ComponentDataT = {
      center: null,
      working_in: null,
      brands: [],
      models: [],
      currentBrand: null,
      extraBreadCrumbs: [],
      banners: null,
      vehiclesData: {}, // Stores all vehicle data
      availableVehicleTypes: [], // Stores available vehicle types
      selectedType: "bus", // Default to show bus
    };
    (async () => {
      const fetchingFns = [getSingleShowroom(+centerID), getBannersFiltered("single_showroom_banner", centerID)];
      const indexes: any = {
        center: 0,
        banners: 1,
      };
      const res = await Promise.all(fetchingFns);
      data.center = res[indexes.center]?.data?.showroom || null;
      data.vehiclesData = res[indexes.center]?.data?.vehicles_data || {};
      data.availableVehicleTypes = Object.keys(data.vehiclesData);
      data.selectedType =
        data.availableVehicleTypes && data.availableVehicleTypes.length > 0 ? data.availableVehicleTypes[0] : "";
      data.banners = res[indexes.banners]?.data?.banners || null;
      data.working_in = res[indexes.center]?.data?.working_in || null;
      data.brands = res[indexes.brands]?.data?.brands || [];
      data.models = res[indexes.models]?.data?.models || [];
      data.currentBrand = res[indexes.currentBrand]?.data?.brand || null;
      if (data.center) {
        data.extraBreadCrumbs.push({
          text: data.center.name,
          link: "",
        });
      }
      initialMount.current = false;
      setComponentData((currentState) => ({
        isFetching: false,
        data: {
          center: data.center || currentState.data.center,
          vehiclesData: data.vehiclesData || currentState.data.vehiclesData,
          availableVehicleTypes: data.availableVehicleTypes || currentState.data.availableVehicleTypes,
          selectedType: data.selectedType || currentState.data.selectedType,
          banners: data.banners || currentState.data.banners,
          working_in: data.working_in || currentState.data.working_in,
          brands: data.brands.length > 0 ? data.brands : currentState.data.brands,
          models: data.models.length > 0 ? data.models : currentState.data.models,
          currentBrand: data.currentBrand || currentState.data.currentBrand,
          extraBreadCrumbs: data.extraBreadCrumbs,
        },
      }));
    })();
  }, [centerID]);

  // Function to filter displayed vehicles
  const filteredVehicles = (condition: "used" | "new" | "imported"): any[] => {
    const { vehiclesData, selectedType } = componentData.data;
    if (!vehiclesData || !vehiclesData[selectedType]) {
      return [];
    }
    const vehiclesArray = vehiclesData[selectedType][condition];
    return vehiclesArray ? vehiclesArray : [];
  };

  return (
    <>
      {/* <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <BreadCrumbs
              pages={[
                { text: locale("home"), link: "/" },
                {
                  text: locale("showrooms"),
                  link: "/showrooms/buses",
                },
                ...(componentData.data.extraBreadCrumbs.length > 0
                  ? componentData.data.extraBreadCrumbs
                  : [{ text: "", link: "" }]),
              ]}
              isLoading={componentData.isFetching}
            />
          </div>
        </div>
      </div> */}
      <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <ShowroomHero
              cover={componentData.data.center?.cover}
              logo={componentData.data.center?.logo}
              name={componentData.data.center?.name}
              note={componentData.data.center?.address}
              phone={componentData.data.center?.phone}
              isLoading={componentData.isFetching}
            />
          </div>
        </div>
      </div>
      <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <div className={`${style.page_row} row`}>
              <div className="col-lg-4">
                {componentData.data.center &&
                  (componentData.data.center?.facebook ||
                    componentData.data.center?.insta ||
                    componentData.data.center?.yt ||
                    componentData.data.center?.website ||
                    (componentData.data.center?.branches && componentData.data.center?.branches.length) ||
                    componentData.data.center?.info) && (
                    <SidebarBox>
                      {componentData.isFetching ? (
                        <LoadingSkeleton count={5} />
                      ) : (
                        <ShowroomSocial
                          social={{
                            fb: componentData.data.center?.facebook,
                            ig: componentData.data.center?.insta,
                            yt: componentData.data.center?.yt,
                            website: componentData.data.center?.website,
                          }}
                          about={componentData.data.center?.info}
                          branches={componentData.data.center?.branches}
                          isServiceCenter={false}
                        />
                      )}
                    </SidebarBox>
                  )}
              </div>
              <div className="col-lg-8">
                {componentData.data.banners && componentData.data.banners.length > 0 && (
                  <div style={{ marginBottom: "20px" }}>
                    <Banner
                      banners={componentData.data.banners.map((banner: any) => ({
                        image: banner.image,
                        url: banner.link,
                      }))}
                      bannerStyle="small"
                      redirection="newPage"
                    />
                  </div>
                )}
                <div className={style.types_header}>
                  {componentData.isFetching ? (
                    <LoadingSkeleton count={2} width="100%" />
                  ) : (
                    <VehicleTypeSelector
                      active={componentData.data.selectedType}
                      availableVehicles={componentData.data.availableVehicleTypes}
                      onClickHandler={(type: VehicleTypesT) =>
                        setComponentData((prev) => ({
                          ...prev,
                          data: { ...prev.data, selectedType: type },
                        }))
                      }
                    />
                  )}
                </div>

                {componentData.isFetching ? (
                  <LoadingSkeleton count={8} />
                ) : filteredVehicles("new").length === 0 &&
                  filteredVehicles("used").length === 0 &&
                  filteredVehicles("imported").length === 0 ? (
                  <div className="col-12">
                    <EmptyContent>
                      <p>{locale("noResult")}</p>
                    </EmptyContent>
                  </div>
                ) : (
                  <>
                    {filteredVehicles("new") && filteredVehicles("new").length > 0 && (
                      <div className={style.page_section}>
                        <div className={style.controllers}>
                          <span>{locale("new")}</span>
                        </div>
                        <div className={`${style.all_used_vehicles} row`}>
                          {filteredVehicles("new").map((vehicle) => (
                            <div key={vehicle.id} className="col-md-6">
                              <SingleUsedVehicleCard
                                showroom={{
                                  is_showroom: false,
                                  image: vehicle?.showroom?.logo || vehicle?.seller?.showroom?.logo,
                                  link: `/showrooms/${vehicle?.showroom?.id}`,
                                }}
                                image={vehicle?.images[0]?.image}
                                link={`/${vehicle.condition.code}-${
                                  vehicleModulesMap[componentData.data.selectedType].plural
                                }/${vehicleModulesMap[componentData.data.selectedType].single}/${vehicle.id}`}
                                isFeatured={vehicle.listing_period.is_featured}
                                date={vehicle?.created_at}
                                location={
                                  vehicle?.region ? `${vehicle?.region?.name}, ${vehicle?.region?.city?.name}` : ""
                                }
                                name={`${vehicle?.model?.brand?.name} ${vehicle?.model?.name} ${vehicle?.model?.year}`}
                                price={vehicle?.price}
                                phone={componentData.data.center?.phone}
                                category={vehicle?.model?.category}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {filteredVehicles("used") && filteredVehicles("used").length > 0 && (
                      <div className={style.page_section}>
                        <div className={style.controllers}>
                          <span>{locale("used")}</span>
                        </div>
                        <div className={`${style.all_used_vehicles} row`}>
                          {filteredVehicles("used").map((vehicle) => (
                            <div key={vehicle.id} className="col-md-6">
                              <SingleUsedVehicleCard
                                showroom={{
                                  is_showroom: false,
                                  image: vehicle?.showroom?.logo || vehicle?.seller?.showroom?.logo,
                                  link: `/showrooms/${vehicle?.showroom?.id}`,
                                }}
                                image={vehicle?.images[0]?.image}
                                link={`/${vehicle.condition.code}-${
                                  vehicleModulesMap[componentData.data.selectedType].plural
                                }/${vehicleModulesMap[componentData.data.selectedType].single}/${vehicle.id}`}
                                isFeatured={vehicle.listing_period.is_featured}
                                date={vehicle?.created_at}
                                location={
                                  vehicle?.region ? `${vehicle?.region?.name}, ${vehicle?.region?.city?.name}` : ""
                                }
                                name={`${vehicle?.model?.brand?.name} ${vehicle?.model?.name} ${vehicle?.model?.year}`}
                                price={vehicle?.price}
                                phone={componentData.data.center?.phone}
                                category={vehicle?.model?.category}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {filteredVehicles("imported") && filteredVehicles("imported").length > 0 && (
                      <div className={style.page_section}>
                        <div className={style.controllers}>
                          <span>{locale("imported")}</span>
                        </div>
                        <div className={`${style.all_used_vehicles} row`}>
                          {filteredVehicles("imported").map((vehicle) => (
                            <div key={vehicle.id} className="col-md-6">
                              <SingleUsedVehicleCard
                                showroom={{
                                  is_showroom: false,
                                  image: vehicle?.showroom?.logo || vehicle?.seller?.showroom?.logo,
                                  link: `/showrooms/${vehicle?.showroom?.id}`,
                                }}
                                image={vehicle?.images[0]?.image}
                                link={`/${vehicle.condition.code}-${
                                  vehicleModulesMap[componentData.data.selectedType].plural
                                }/${vehicleModulesMap[componentData.data.selectedType].single}/${vehicle.id}`}
                                isFeatured={vehicle.listing_period.is_featured}
                                date={vehicle?.created_at}
                                location={
                                  vehicle?.region ? `${vehicle?.region?.name}, ${vehicle?.region?.city?.name}` : ""
                                }
                                name={`${vehicle?.model?.brand?.name} ${vehicle?.model?.name} ${vehicle?.model?.year}`}
                                price={vehicle?.price}
                                phone={componentData.data.center?.phone}
                                category={vehicle?.model?.category}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SingleShowroom;
